import React, { useEffect, useRef, useState } from "react";
import "./BatteryShowCase.css";

/* ─────────────────────────────────────────────
   Feature definitions — left column & right column
   ───────────────────────────────────────────── */
const LEFT_FEATURES = [
  {
    id: "bms",
    icon: "⚡",
    title: "Active Cell Balancing BMS",
    desc: "Smart BMS keeps every cell in perfect equilibrium — no overcharge, no over-discharge, ever.",
  },
  {
    id: "temp",
    icon: "🌡️",
    title: "Multi-Layer Temperature Protection",
    desc: "Triple thermal sensors cut power instantly if temps cross safe thresholds.",
  },
  {
    id: "cells",
    icon: "🔋",
    title: "A+ Grade Cells Only",
    desc: "We refuse B-grade. Every pack uses first-life, BIS-certified lithium cells with rated capacity.",
  },
];

const RIGHT_FEATURES = [
  {
    id: "wires",
    icon: "🔌",
    title: "High-Gauge Copper Wiring",
    desc: "Pure copper, high-strand wires rated for sustained high-current delivery without heat loss.",
  },
  {
    id: "cycles",
    icon: "♻️",
    title: "2000+ Cycle Lifespan",
    desc: "Engineered to outlast lead-acid 5× over. Full DoD without degradation for years.",
  },
  {
    id: "safe",
    icon: "🛡️",
    title: "Short-Circuit & Surge Protection",
    desc: "Instantaneous fault detection with auto-recovery. Your inverter and appliances stay safe.",
  },
];

/* ─────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────── */
const BatteryShowcase = ({ productImage }) => {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);

  /* Trigger entrance animation on scroll into view */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  /* Subtle parallax on mouse move */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      const img = el.querySelector(".bsv2-product-img");
      const glow = el.querySelector(".bsv2-core-glow");
      if (img)
        img.style.transform = `translate(${dx * 12}px, ${dy * 10}px) scale(1.02)`;
      if (glow) glow.style.transform = `translate(${dx * 20}px, ${dy * 15}px)`;
    };
    const onLeave = () => {
      const img = el.querySelector(".bsv2-product-img");
      const glow = el.querySelector(".bsv2-core-glow");
      if (img) img.style.transform = "";
      if (glow) glow.style.transform = "";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const imgSrc =
    productImage || "https://placehold.co/600x600/111/222?text=Battery";

  return (
    <section
      className={`bsv2-root${visible ? " bsv2-visible" : ""}`}
      ref={rootRef}
    >
      {/* ── ambient background ── */}
      <div className="bsv2-bg">
        <div className="bsv2-bg-grid" />
        <div className="bsv2-bg-vignette" />
      </div>

      {/* ── top label ── */}
      <div className="bsv2-eyebrow">
        <span className="bsv2-eyebrow-dot" />
        ENGINEERED FOR POWER
      </div>

      {/* ── headline ── */}
      <h2 className="bsv2-headline">
        Built different.
        <br />
        <em>Inside and out.</em>
      </h2>

      {/* ── three-column layout ── */}
      <div className="bsv2-stage">
        {/* LEFT features */}
        <div className="bsv2-col bsv2-col-left">
          {LEFT_FEATURES.map((f, i) => (
            <FeatureCard
              key={f.id}
              feature={f}
              delay={i * 100}
              align="right"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>

        {/* CENTER — product hero */}
        <div className="bsv2-center">
          <div className="bsv2-core-glow" />
          <div className="bsv2-ring bsv2-ring-1" />
          <div className="bsv2-ring bsv2-ring-2" />
          <img
            src={imgSrc}
            alt="Zynvert Lithium Battery"
            className="bsv2-product-img"
            onError={(e) =>
              (e.target.src =
                "https://placehold.co/500x500/111/333?text=Battery")
            }
          />
          {/* center badge */}
          <div className="bsv2-center-badge">
            <span className="bsv2-badge-pulse" />
            <span className="bsv2-badge-text">BMS Active</span>
          </div>
        </div>

        {/* RIGHT features */}
        <div className="bsv2-col bsv2-col-right">
          {RIGHT_FEATURES.map((f, i) => (
            <FeatureCard
              key={f.id}
              feature={f}
              delay={i * 100 + 150}
              align="left"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>
      </div>

      {/* ── bottom spec strip ── */}
      <div className="bsv2-spec-strip">
        {[
          ["2000+", "Charge Cycles"],
          ["100%", "Depth of Discharge"],
          ["BIS", "Certified Cells"],
          ["≤2s", "Fault Response"],
          ["0", "Maintenance Required"],
        ].map(([val, label]) => (
          <div className="bsv2-spec" key={label}>
            <span className="bsv2-spec-val">{val}</span>
            <span className="bsv2-spec-label">{label}</span>
          </div>
        ))}
      </div>

      {/* ── footnote ── */}
      <p className="bsv2-footnote">
        * All specifications reflect tested performance under standard
        conditions. Individual results may vary.
      </p>
    </section>
  );
};

/* ─────────────────────────────────────────────
   Feature card sub-component
   ───────────────────────────────────────────── */
const FeatureCard = ({ feature, delay, align, active, onHover }) => (
  <div
    className={`bsv2-feat bsv2-feat-${align}${active ? " bsv2-feat-active" : ""}`}
    style={{ transitionDelay: `${delay}ms`, animationDelay: `${delay}ms` }}
    onMouseEnter={() => onHover(feature.id)}
    onMouseLeave={() => onHover(null)}
  >
    <div className="bsv2-feat-icon-wrap">
      <span className="bsv2-feat-icon">{feature.icon}</span>
    </div>
    <div className="bsv2-feat-body">
      <p className="bsv2-feat-title">{feature.title}</p>
      <p className="bsv2-feat-desc">{feature.desc}</p>
    </div>
  </div>
);

export default BatteryShowcase;
